import type {
  IconListItem,
  IconListLayout,
  IconListProps,
  IconListSize,
} from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { ColorField, SelectField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

const LAYOUT = ["vertical", "horizontal"] as const;
const SIZE = ["sm", "md", "lg"] as const;

function newItemId(): string {
  return `i-${Math.random().toString(36).slice(2, 8)}`;
}

export function IconListInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as IconListProps;
  const items = p.items ?? [];

  const setItems = (next: IconListItem[]) => update(widget.id, { items: next });

  const addItem = () =>
    setItems([...items, { id: newItemId(), icon: "✓", text: "New item" }]);

  const updateItem = (id: string, patch: Partial<IconListItem>) =>
    setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const removeItem = (id: string) => setItems(items.filter((it) => it.id !== id));

  const moveItem = (id: string, dir: "up" | "down") => {
    const idx = items.findIndex((it) => it.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setItems(next);
  };

  return (
    <>
      <SelectField
        label="Layout"
        value={p.layout ?? "vertical"}
        options={LAYOUT}
        onChange={(v) => update(widget.id, { layout: v as IconListLayout })}
      />
      <SelectField
        label="Size"
        value={p.size ?? "md"}
        options={SIZE}
        onChange={(v) => update(widget.id, { size: v as IconListSize })}
      />
      <ColorField
        label="Icon colour"
        value={p.iconColor ?? "#ffffff"}
        onChange={(v) => update(widget.id, { iconColor: v })}
      />
      <ColorField
        label="Badge colour"
        value={p.badgeColor ?? "#111827"}
        onChange={(v) => update(widget.id, { badgeColor: v })}
      />
      <ColorField
        label="Text colour"
        value={p.textColor ?? "#111827"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Items ({items.length})
      </div>

      {items.map((it, i) => (
        <div
          key={it.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "up")}
              disabled={i === 0}
              title="Move up"
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "down")}
              disabled={i === items.length - 1}
              title="Move down"
              style={{
                padding: "2px 6px",
                opacity: i === items.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeItem(it.id)}
              title="Delete item"
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>
          <div className="emke-inspector-grid-2" style={{ padding: "0" }}>
            <TextField
              label={`Icon ${i + 1}`}
              value={it.icon}
              onChange={(v) => updateItem(it.id, { icon: v })}
            />
            <TextField
              label="Text"
              value={it.text}
              onChange={(v) => updateItem(it.id, { text: v })}
            />
          </div>
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addItem}
          style={{ width: "100%" }}
        >
          + Add item
        </button>
      </div>
    </>
  );
}
